import { useState, useEffect } from 'react';
import { X, Percent, Tag, Check, ChevronLeft } from 'lucide-react';
import { applyDiscount } from '../lib/discounts';
import { DiscountCodeService } from '../services/discountCodeService';
import { useBusiness } from '../contexts/BusinessContext';

const isMobile = () => window.innerWidth < 640;

interface ApplyDiscountModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string;
  bookingTotal: number;
  onApply: () => Promise<void>;
  existingDiscounts?: Array<{
    id: string;
    title: string;
    type: 'percentage' | 'fixed';
    amount: number;
  }>;
}

export default function ApplyDiscountModal({
  isOpen,
  onClose,
  bookingId,
  bookingTotal,
  onApply,
  existingDiscounts = []
}: ApplyDiscountModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [discountMethod, setDiscountMethod] = useState<'manual' | 'code'>('code');
  const [discountCode, setDiscountCode] = useState('');
  const [codeValidation, setCodeValidation] = useState<{
    isValid: boolean;
    code?: any;
    error?: string;
    discountAmount?: number;
  } | null>(null);
  const [validatingCode, setValidatingCode] = useState(false);
  const [formData, setFormData] = useState<{
    title: string;
    type: 'percentage' | 'fixed';
    amount: string;
  }>({
    title: '',
    type: 'percentage',
    amount: ''
  });

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setError(null);
      setDiscountMethod('code');
      setDiscountCode('');
      setCodeValidation(null);
      setValidatingCode(false);
      setFormData({
        title: '',
        type: 'percentage',
        amount: ''
      });
    }
  };

  const calculateDiscountAmount = () => {
    const amount = parseFloat(formData.amount as string) || 0;
    if (formData.type === 'percentage') {
      // Round to 2 decimal places for consistent calculation
      const discountAmount = Math.round((bookingTotal * (amount / 100)) * 100) / 100;
      return Math.min(discountAmount, bookingTotal); // Prevent discount from exceeding total
    }
    return Math.min(amount, bookingTotal); // Prevent discount from exceeding total
  };

  const validateDiscountCode = async (code: string) => {
    if (!selectedBusiness?.id || !code.trim()) {
      setCodeValidation(null);
      return;
    }

    setValidatingCode(true);
    setError(null);

    try {
      const validation = await DiscountCodeService.validateDiscountCode(
        selectedBusiness.id,
        code,
        bookingTotal
      );
      setCodeValidation(validation);
    } catch (error) {
      console.error('Error validating discount code:', error);
      setCodeValidation({
        isValid: false,
        error: 'Failed to validate discount code'
      });
    } finally {
      setValidatingCode(false);
    }
  };

  const handleDiscountCodeChange = (code: string) => {
    setDiscountCode(code);
    
    // Debounce validation
    const timeoutId = setTimeout(() => {
      validateDiscountCode(code);
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  const handleApplyDiscountCode = async () => {
    if (!selectedBusiness?.id || !codeValidation?.isValid || !codeValidation.code) return;

    try {
      setLoading(true);
      setError(null);

      const result = await DiscountCodeService.applyDiscountCode(
        bookingId,
        selectedBusiness.id,
        discountCode,
        bookingTotal
      );

      if (result.success) {
        await onApply();
        onClose();
      } else {
        setError(result.error || 'Failed to apply discount code');
      }
    } catch (err) {
      console.error('Error applying discount code:', err);
      setError('Failed to apply discount code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyDiscount = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      setError(null);

      await applyDiscount(
        bookingId,
        formData.title,
        formData.type,
        parseFloat(formData.amount) || 0
      );

      await onApply();
      onClose();
    } catch (err) {
      console.error('Error applying discount:', err);
      setError(err instanceof Error ? err.message : 'Failed to apply discount');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const mobile = isMobile();

  return (
    <div 
      className={`fixed inset-0 transition-opacity duration-300 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      } z-[999999]`}
      style={{
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={onClose}
    >
      <div 
        className={`bg-[#F2F2F7] transform transition-all duration-300 ease-out ${
          mobile
            ? `fixed inset-0 ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${isOpen ? 'translate-x-0' : 'translate-x-full'}`
        } z-[999999]`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          {/* iOS-style Navigation Header */}
          <div className="bg-[#F2F2F7] border-b border-gray-300/60">
            <div className="relative flex items-center justify-center h-[44px]">
              <button
                onClick={onClose}
                className="absolute left-2 flex items-center text-[#007AFF] active:opacity-50 transition-opacity px-2 h-full"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <ChevronLeft className="h-5 w-5 -ml-1" strokeWidth={2.5} />
                <span className="text-[17px]">Back</span>
              </button>
              <h2 className="text-[17px] font-semibold text-black">Apply Discount</h2>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="px-4 pt-6 pb-8 space-y-6">
              {error && (
                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-[12px] text-[14px]">
                  {error}
                </div>
              )}

              {/* Existing Discount Warning */}
              {existingDiscounts.length > 0 && (
                <div className="bg-orange-50 px-4 py-3 rounded-[12px]">
                  <p className="text-[14px] font-semibold text-orange-800">Existing Discount Will Be Replaced</p>
                  <div className="mt-1.5 text-[13px] text-orange-700 space-y-1">
                    {existingDiscounts.map((discount) => (
                      <p key={discount.id}>
                        {discount.title} — {discount.type === 'percentage' ? `${discount.amount}%` : `$${discount.amount}`}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Discount Method - iOS Segmented Control Style */}
              <div>
                <p className="text-[13px] font-medium text-gray-500 uppercase tracking-wide px-4 mb-2">Discount Method</p>
                <div className="bg-[#E8E8ED] rounded-[9px] p-[2px] flex">
                  <button
                    type="button"
                    onClick={() => setDiscountMethod('code')}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-[7px] rounded-[7px] text-[13px] font-medium transition-all ${
                      discountMethod === 'code'
                        ? 'bg-white text-black shadow-sm'
                        : 'text-gray-500'
                    }`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Tag className="h-3.5 w-3.5" />
                    Discount Code
                  </button>
                  <button
                    type="button"
                    onClick={() => setDiscountMethod('manual')}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-[7px] rounded-[7px] text-[13px] font-medium transition-all ${
                      discountMethod === 'manual'
                        ? 'bg-white text-black shadow-sm'
                        : 'text-gray-500'
                    }`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Percent className="h-3.5 w-3.5" />
                    Manual
                  </button>
                </div>
              </div>

              {/* Discount Code Input */}
              {discountMethod === 'code' && (
                <div>
                  <p className="text-[13px] font-medium text-gray-500 uppercase tracking-wide px-4 mb-2">Discount Code</p>
                  <div className="bg-white rounded-[12px]">
                    <div className="relative">
                      <input
                        type="text"
                        value={discountCode}
                        onChange={(e) => handleDiscountCodeChange(e.target.value.toUpperCase())}
                        className="w-full h-[44px] px-4 bg-transparent border-0 text-[17px] font-mono uppercase placeholder-gray-400 focus:ring-0 focus:outline-none box-border"
                        placeholder="Enter code"
                      />
                      <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                        {validatingCode && (
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-[#007AFF] rounded-full animate-spin" />
                        )}
                        {!validatingCode && codeValidation?.isValid && (
                          <Check className="h-5 w-5 text-[#34C759]" />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {codeValidation && !validatingCode && (
                    <div className="mt-2 px-4">
                      {codeValidation.isValid ? (
                        <div className="space-y-0.5">
                          <p className="text-[13px] text-[#34C759] flex items-center gap-1">
                            <Check className="h-3.5 w-3.5" />
                            Valid discount code
                          </p>
                          {codeValidation.code?.description && (
                            <p className="text-[12px] text-gray-500">{codeValidation.code.description}</p>
                          )}
                          <p className="text-[12px] text-gray-500">
                            {codeValidation.code?.type === 'percentage' 
                              ? `${codeValidation.code.value}%` 
                              : `$${codeValidation.code?.value}`}
                            {codeValidation.discountAmount != null && (
                              <span className="ml-1 font-medium text-[#34C759]">(-${codeValidation.discountAmount.toFixed(2)})</span>
                            )}
                          </p>
                        </div>
                      ) : (
                        <p className="text-[13px] text-[#FF3B30] flex items-center gap-1">
                          <X className="h-3.5 w-3.5" />
                          {codeValidation.error}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Code Preview Card */}
                  {codeValidation?.isValid && codeValidation.discountAmount != null && (
                    <div className="mt-4 bg-white rounded-[12px] p-4">
                      <p className="text-[13px] font-medium text-gray-500 mb-2">Preview</p>
                      <div className="flex justify-between items-center">
                        <span className="text-[15px] text-gray-600">Discount</span>
                        <span className="text-[15px] font-semibold text-[#34C759]">-${codeValidation.discountAmount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center mt-1.5 pt-1.5 border-t border-gray-100">
                        <span className="text-[15px] text-gray-600">New Total</span>
                        <span className="text-[15px] font-semibold text-black">${(bookingTotal - codeValidation.discountAmount).toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Manual Discount Fields */}
              {discountMethod === 'manual' && (
                <div className="space-y-6">
                  <div>
                    <p className="text-[13px] font-medium text-gray-500 uppercase tracking-wide px-4 mb-2">Discount Name</p>
                    <div className="bg-white rounded-[12px]">
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full h-[44px] px-4 bg-transparent border-0 text-[17px] placeholder-gray-400 focus:ring-0 focus:outline-none box-border"
                        placeholder="e.g. Loyalty discount"
                      />
                    </div>
                  </div>

                  <div>
                    <p className="text-[13px] font-medium text-gray-500 uppercase tracking-wide px-4 mb-2">Discount Type</p>
                    <div className="bg-[#E8E8ED] rounded-[9px] p-[2px] flex">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, type: 'percentage' }))}
                        className={`flex-1 py-[7px] rounded-[7px] text-[13px] font-medium transition-all ${
                          formData.type === 'percentage'
                            ? 'bg-white text-black shadow-sm'
                            : 'text-gray-500'
                        }`}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        Percentage
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, type: 'fixed' }))}
                        className={`flex-1 py-[7px] rounded-[7px] text-[13px] font-medium transition-all ${
                          formData.type === 'fixed'
                            ? 'bg-white text-black shadow-sm'
                            : 'text-gray-500'
                        }`}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        Fixed Amount
                      </button>
                    </div>
                  </div>

                  <div>
                    <p className="text-[13px] font-medium text-gray-500 uppercase tracking-wide px-4 mb-2">
                      {formData.type === 'percentage' ? 'Percentage' : 'Amount'}
                    </p>
                    <div className="bg-white rounded-[12px]">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <span className="text-[17px] text-gray-400">
                            {formData.type === 'percentage' ? '%' : '$'}
                          </span>
                        </div>
                        <input
                          type="number"
                          min="0"
                          step={formData.type === 'percentage' ? '1' : '0.01'}
                          max={formData.type === 'percentage' ? "100" : undefined}
                          value={formData.amount}
                          onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                          className="w-full h-[44px] pl-9 pr-4 bg-transparent border-0 text-[17px] placeholder-gray-400 focus:ring-0 focus:outline-none box-border"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>

                  {parseFloat(formData.amount as string) > 0 && (
                    <div className="bg-white rounded-[12px] p-4">
                      <p className="text-[13px] font-medium text-gray-500 mb-2">Preview</p>
                      <div className="flex justify-between items-center">
                        <span className="text-[15px] text-gray-600">Discount</span>
                        <span className="text-[15px] font-semibold text-[#34C759]">-${calculateDiscountAmount().toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center mt-1.5 pt-1.5 border-t border-gray-100">
                        <span className="text-[15px] text-gray-600">New Total</span>
                        <span className="text-[15px] font-semibold text-black">${(bookingTotal - calculateDiscountAmount()).toLocaleString()}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* iOS-style Footer */}
          <div className="px-4 py-3 bg-[#F2F2F7] border-t border-gray-300/60">
            <button
              onClick={discountMethod === 'code' ? handleApplyDiscountCode : handleApplyDiscount}
              disabled={
                loading || 
                (discountMethod === 'manual' && (!formData.title || !formData.amount || parseFloat(formData.amount) <= 0)) ||
                (discountMethod === 'code' && (!codeValidation?.isValid || !discountCode.trim()))
              }
              className="w-full h-[50px] text-[17px] font-semibold text-white bg-[#007AFF] rounded-[12px] active:opacity-70 disabled:opacity-40 disabled:active:opacity-40 transition-opacity"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {loading ? 'Applying...' : `Apply ${discountMethod === 'code' ? 'Code' : 'Discount'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}